import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Container } from "@mui/material";
import {
  Add as AddIcon,
  Agriculture as AgricultureIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { useAlert } from "../../components/Alert";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import GenericButton from "../../components/Custom/GenericButton";
import CustomCard from "../../components/Famer/GenericCard";
import api from "../../globals/axios";
import LoaderContainer from "../../components/LoaderContainer";

const POPProgramsList = () => {
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  useEffect(() => {
    fetchPrograms();
  }, []);

  const fetchPrograms = async () => {
    try {
      const { data } = await api("/farm-activities/create-program/");
      setPrograms(data.data);
    } catch (error) {
      console.error("Error fetching programs:", error);
      showAlert("error", t("Failed to fetch programs"));
    } finally {
      setLoading(false);
    }
  };

  const onCreateProgram = () => {
    navigate("/pop-manager/create-program");
  };

  const onViewDetails = (programId) => {
    navigate(`/pop-manager/program/${programId}`);
  };

  return (
    <LoaderContainer loading={loading}>
      <Container
        maxWidth="xs"
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          minHeight: "calc(100vh - 4rem)",
          justifyContent: "space-between",
        }}
      >
        {/* Header */}

        <CustomTitle>{t("Programs List")}</CustomTitle>
        {programs.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
            }}
          >
            <AgricultureIcon
              sx={{
                fontSize: 120,
                color: "text.secondary",
                mb: 2,
              }}
            />
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              color="text.secondary"
            >
              {t("No Programs Found")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {t("Get started by creating program")}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Programs Grid or Empty State */}
            {isNotNullOrEmptyArray(programs) && (
              <Grid
                container
                sx={{ maxHeight: "calc(100vh - 16rem)", overflowY: "auto" }}
              >
                {programs.map((program) => {
                  return (
                    <Grid item xs={12} key={program.id}>
                      <CustomCard
                        key={program.id}
                        id={program.id}
                        t={t}
                        data={{
                          [t("Name")]: program.name,
                          [t("Crop")]: program.commodity_details?.name || "N/A",
                          [t("Stages")]: program.stages_count || 0,
                          [t("Activities")]: program.activities_count || 0,
                          ...(program.commodity_details?.name.toLowerCase() ===
                            "sugarcane" && {
                            [t("Ratoon Crop")]: program.is_ratoon
                              ? t("Yes")
                              : t("No"),
                            [t("Cycle")]: program.cycle || "N/A",
                          }),
                        }}
                        handlePrimaryButton={onViewDetails}
                        primaryLabel={t("View Stages")}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </>
        )}

        <GenericButton
          backgroundColor="primary"
          fontColor="#fff"
          icon={AddIcon}
          padding="6px 0px"
          onClick={onCreateProgram}
        >
          {t("Create Program")}
        </GenericButton>
      </Container>
    </LoaderContainer>
  );
};

export default POPProgramsList;
